import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Field, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { type IMerchant } from '../../models/IMerchant';
import styles from './index.module.css';
import { useAppSelector } from '../../hooks/redux';
import { baseUrl } from '../../Consts';
import { isLink, sanitizeData } from '../../Utils';

const phoneRegExp = /^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){10,12}(\s*)?$/;

interface IProps {
	merchant: IMerchant;
}

interface IValues {
	amount: string;
	currency: string;
	phone?: string;
	email?: string;
	orderId?: string;
	userName?: string;
	productName?: string;
	orderComment?: string;
}

interface IRequestParams {
	merchant_id: number;
	amount: number;
	currency: string;
	phone?: string;
	email?: string;
	orderId?: string;
	userName?: string;
	productName?: string;
	orderComment?: string;
}

export const PaymentInfoPanel = ({ merchant }: IProps) => {
	const { language } = useAppSelector(state => state.languageSlice);
	const { t, i18n } = useTranslation('translation');
	const { currencies } = useAppSelector(state => state.currenciesSlice);
	const location = window.location.toString();

	const renderPhoneField = merchant?.has_phone_field;
	const renderOrderIdField = merchant?.has_order_id_field;
	const renderEmailField = merchant?.has_email_field;
	const renderUserNameField = merchant?.has_user_name_field;
	const renderProductNameField = merchant?.has_product_name_field;
	const renderOrderCommentField = merchant?.has_order_comment_field;

	const getInitialValues = (): IValues => {
		let localeCurrency = currencies[0];
		if (location.includes('uz')) {
			localeCurrency = currencies.find(element => element === 'UZS');
		}

		return ({
			amount: '', currency: localeCurrency, phone: '', email: '', orderId: '',
			userName: '', productName: '', orderComment: ''
		});
	};

	function getSchema () {
		const Schema = Yup.object().shape({
			amount: Yup.number()
				.typeError('Укажите корректную сумму')
				.required('Укажите сумму'),
			phone: renderPhoneField
				? Yup.string()
					.matches(phoneRegExp, 'Укажите корректный номер телефона')
					.required('Укажите номер телефона')
				: null,
			orderId: renderOrderIdField
				? Yup.string()
					.max(50, 'Номер заказа не должен быть более 50 символов')
					.required('Укажите номер заказа')
				: null,
			email: renderEmailField
				? Yup.string().email('Укважите существующий email').required('Укажите email')
				: null,
			userName: renderUserNameField
				? Yup.string()
					.required('Укажите Ваше имя')
				: null,
			productName: renderProductNameField
				? Yup.string()
					.required('Укажите наименование товара или услуги')
				: null,
			orderComment: renderOrderCommentField
				? Yup.string()
					.notRequired()
				: null,
		});

		return Schema;
	}

	useEffect(() => {
		i18n.changeLanguage();
	}, [language]);

	function getClearPhone (phone: string): string {
		const regEx = /[^+\d]/g;

		return phone.replace(regEx, '');
	}

	async function handleSubmit (values: IValues, actions: FormikHelpers<IValues>) {
		const { setSubmitting, resetForm } = actions;
		const requestParams: IRequestParams = {
			merchant_id: merchant.id,
			amount: Number(values.amount),
			currency: values.currency,
			orderId: values.orderId,
			phone: getClearPhone(values.phone),
			email: values.email,
			userName: values.userName,
			productName: values.productName,
			orderComment: values.orderComment,
		};

		setSubmitting(true);

		await axios.post(`${baseUrl}/init_payment`, sanitizeData(requestParams)).then(response => {
			if (isLink(response.data)) {
				window.open(response.data);
			}

			resetForm({ values: getInitialValues() });
			setSubmitting(false);
		});
	}

	return (
		<div className="info_panel">
			<Formik
				validationSchema={getSchema()}
				initialValues={getInitialValues()}
				onSubmit={handleSubmit}
			>
				{({errors, touched}) => (
					<Form className={styles.form}>
						<label htmlFor="amount" className={styles.label}>{t('Сумма оплаты')}</label>
						<div className={styles.currency_and_amount}>
							<Field id="amount" name="amount" className={styles.amount} placeholder="0.00" />
							<label htmlFor="currency" className={styles.hidden}>{t('Валюта')}</label>
							<Field id="currency" name="currency" as="select" className={styles.currency}>
								{
									map(currencies, c => (
										<option value={c} key={c}>{c}</option>
									))
								}
							</Field>
						</div>
						{errors.amount && touched.amount ? (<div className={styles.error}>{errors.amount}</div>) : null}

						{renderOrderIdField ? (
							<>
								<label htmlFor="orderId" className={styles.label}>{t('Номер заказа')}</label>
								<Field id="orderId" name="orderId" placeholder="№" className={styles.input} />
							</>
						) : null}

						{errors.orderId && touched.orderId ? (<div className={styles.error}>{errors.orderId}</div>) : null}

						{renderPhoneField ? (
							<>
								<label htmlFor="phone" className={styles.label}>{t('Телефон')}</label>
								<Field id="phone" name="phone" placeholder="+9 (999) 999-99-99" className={styles.input} />
							</>
						) : null}

						{errors.phone && touched.phone ? (<div className={styles.error}>{errors.phone}</div>) : null}

						{renderEmailField ? (
							<>
								<label htmlFor="email" className={styles.label}>{t('Email')}</label>
								<Field id="email" name="email" placeholder={t('Введите Ваш email')} className={styles.input} />
							</>
						) : null}

						{errors.email && touched.email ? (<div className={styles.error}>{errors.email}</div>) : null}

						{renderUserNameField ? (
							<>
								<label htmlFor="userName" className={styles.label}>{t('Ваше имя')}</label>
								<Field id="userName" name="userName" placeholder={t('Введите Ваше имя')} className={styles.input} />
							</>
						) : null}

						{errors.userName && touched.userName ? (<div className={styles.error}>{errors.userName}</div>) : null}

						{renderProductNameField ? (
							<>
								<label htmlFor="productName" className={styles.label}>{t('Наименование товара или услуги')}</label>
								<Field id="productName" name="productName" placeholder={t('Введите наименование товара или услуги')} className={styles.input} />
							</>
						) : null}

						{errors.productName && touched.productName ? (<div className={styles.error}>{errors.productName}</div>) : null}

						{renderOrderCommentField ? (
							<>
								<label htmlFor="orderComment" className={styles.label}>{t('Комментарий к заказу')}</label>
								<Field id="orderComment" name="orderComment" placeholder={t('Введите комментарий к заказу')} className={styles.input} />
							</>
						) : null}

						{errors.orderComment && touched.orderComment ? (<div className={styles.error}>{errors.orderComment}</div>) : null}

						<button disabled={!isEmpty(errors)} type="submit" className={styles.submit}>{t('Перейти к оплате')}</button>

						<Link to="/" className={styles.back_link}>{t('Отмена')}</Link>
					</Form>
				)}
			</Formik>
		</div>
	);
};
